/**
 * @file Renders the additional-details row of an investment profile description.
 */
import React from "react";
import type { ReactNode } from "react";
import { Space, Typography } from "antd";

import renderBoolean from "./renderBoolean";

const { Text } = Typography;

type Profile = {
  isLiabilityAccepted?: boolean;
  hasPurchasedExemptSecurities?: boolean;
};

type AdditionalItem = {
  span: number;
  label: string;
  value: ReactNode;
};

const LABEL_TITLE = "Additional Details";
const LABEL_HAS_PURCHASED = "I previously purchased securities exempt from federal and state registration.";
const LABEL_OWN_DECISIONS = `I make my own investment decisions with respect to the investments in
 marketable securities, limited partnerships, limited liability companies
 or other private investments.
`;
const LABEL_HAS_NOT_PURCHASED = "I didn't purchase securities exempt from federal and state registration.";
const LABEL_NOT_OWN_DECISIONS = "I do not make my own investment decisions.";

const TEXT_FONT_SIZE = 12;
const LINE_HEIGHT = 1.3;
const FULL_ROW_SPAN = 2;

/**
 * Renders a line of the additional-details text in the secondary, small style.
 */
const renderText = (value: string): ReactNode => (
  <Text type="secondary" style={{ fontSize: TEXT_FONT_SIZE }}>
    {value}
  </Text>
);

/**
 * Builds the additional-details description row for an investment profile.
 */
const renderAdditional = (
  { isLiabilityAccepted, hasPurchasedExemptSecurities }: Profile,
  extraItems?: ReactNode,
): AdditionalItem => {
  const style = {
    lineHeight: LINE_HEIGHT,
  };

  const additional = (
    <Space
      size={0}
      style={style}
      direction="vertical"
    >
      {
        renderBoolean(
          hasPurchasedExemptSecurities,
          renderText(LABEL_HAS_PURCHASED),
          renderText(LABEL_HAS_NOT_PURCHASED),
        )
      }

      {
        renderBoolean(
          isLiabilityAccepted,
          renderText(LABEL_OWN_DECISIONS),
          renderText(LABEL_NOT_OWN_DECISIONS),
        )
      }

      {
        extraItems && (
          <>{extraItems}</>
        )
      }
    </Space>
  );

  return {
    span: FULL_ROW_SPAN,
    label: LABEL_TITLE,
    value: additional,
  };
};

export default renderAdditional;
